import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import {
  Phone,
  MessageCircle,
  MapPin,
  Clock,
  ShieldCheck,
  Wrench,
  Zap,
  BadgeDollarSign,
  Truck,
  CarFront,
  Warehouse,
  Gavel,
  Route,
  Gem,
  LifeBuoy,
  Siren,
  BatteryCharging,
  KeyRound,
  RotateCcw,
} from "lucide-react";

const WA_LINK =
  "https://wa.me/5521974572840?text=" +
  encodeURIComponent("Olá! Preciso de um reboque agora. Podem me atender?");
const TEL_LINK = "tel:+5521974572840";
const PHONE_DISPLAY = "(21) 97457-2840";

const EASE = [0.16, 1, 0.3, 1];

const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const FadeUp = ({ children, delay = 0, className = "", ...rest }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: EASE }}
    {...rest}
  >
    {children}
  </motion.div>
);

const SERVICES = [
  { icon: Truck, title: "Reboque 24 Horas", desc: "Guincho disponível dia e noite, todos os dias, em qualquer ponto do Rio." },
  { icon: CarFront, title: "Remoção de Veículos", desc: "Remoção rápida e segura de carros, motos e utilitários." },
  { icon: Warehouse, title: "Retirada de Veículos de Pátios", desc: "Retiramos seu veículo de pátios com agilidade e sem burocracia." },
  { icon: Gavel, title: "Retirada de Veículos de Leilões", desc: "Transporte de veículos arrematados em leilões com total cuidado." },
  { icon: Route, title: "Transporte de Veículos", desc: "Transporte entre cidades e bairros com segurança e pontualidade." },
  { icon: Gem, title: "Transporte de Alto Valor", desc: "Cuidado especial para veículos de luxo, clássicos e esportivos." },
  { icon: LifeBuoy, title: "Resgate de Veículos", desc: "Resgate em situações difíceis: ribanceiras, alagamentos e atolamentos." },
  { icon: Siren, title: "Socorro Emergencial", desc: "Pane seca, pane elétrica e emergências. Chegamos rápido onde você estiver." },
  { icon: BatteryCharging, title: "Recarga de Bateria", desc: "Bateria arriou? Fazemos a recarga no local e você segue viagem." },
  { icon: KeyRound, title: "Abertura de Veículos", desc: "Chave presa dentro do carro? Abrimos sem danificar seu veículo." },
  { icon: RotateCcw, title: "Destombamento", desc: "Destombamento de veículos com equipamento adequado e seguro." },
  { icon: Wrench, title: "SOS Automotivo", desc: "Suporte completo para imprevistos na estrada e na cidade." },
];

const DIFFERENTIALS = [
  { icon: Clock, title: "Atendimento 24h", desc: "Estamos disponíveis quando você mais precisa. De madrugada, feriado ou fim de semana: a Zero 21 atende." },
  { icon: ShieldCheck, title: "Equipe Qualificada", desc: "Profissionais preparados para realizar o serviço com segurança, do embarque à entrega." },
  { icon: Wrench, title: "Equipamentos Modernos", desc: "Estrutura adequada para diferentes tipos de veículos, do hatch ao utilitário de alto valor." },
  { icon: Zap, title: "Rápido e Seguro", desc: "Agilidade no atendimento sem abrir mão da segurança do seu veículo em nenhuma etapa." },
  { icon: BadgeDollarSign, title: "Preço Justo e Transparente", desc: "Orçamento claro antes da realização do serviço. Sem surpresas, sem taxas escondidas." },
];

const COVERAGE_ZONES = [
  { zone: "Zona Sul", places: "Copacabana, Ipanema, Leblon, Botafogo, Flamengo, Laranjeiras" },
  { zone: "Centro", places: "Centro, Lapa, Santa Teresa, Gamboa, Santo Cristo, Caju" },
  { zone: "Zona Norte", places: "Tijuca, Méier, Madureira, Penha, Ilha do Governador, Ramos" },
  { zone: "Zona Oeste", places: "Barra da Tijuca, Recreio, Jacarepaguá, Bangu, Campo Grande, Santa Cruz" },
];

const MARQUEE_ITEMS = [
  "Reboque 24 Horas",
  "Guincho Perto de Mim",
  "Atendimento 24h",
  "SOS Automotivo",
  "Rápido e Seguro",
  "7 Dias por Semana",
];

const Header = () => (
  <motion.header
    initial={{ y: -80 }}
    animate={{ y: 0 }}
    transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
    className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-zinc-800/60"
  >
    <div className="relative max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
      <a href="#inicio" className="flex items-center max-md:absolute max-md:left-1/2 max-md:-translate-x-1/2" data-testid="header-logo-link" aria-label="Zero 21 Reboque 24H - Início">
        <img
          src="/assets/logo.png"
          alt="Logo Zero 21 Reboque 24H - Guincho 24 horas Rio de Janeiro"
          className="h-12 md:h-16 w-auto"
        />
      </a>
      <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-zinc-300" aria-label="Navegação principal">
        <a href="#servicos" data-testid="nav-servicos-link" className="hover:text-yellow-400 transition-colors">Serviços</a>
        <a href="#diferenciais" data-testid="nav-diferenciais-link" className="hover:text-yellow-400 transition-colors">Diferenciais</a>
        <a href="#cobertura" data-testid="nav-cobertura-link" className="hover:text-yellow-400 transition-colors">Cobertura</a>
        <a href="#contato" data-testid="nav-contato-link" className="hover:text-yellow-400 transition-colors">Contato</a>
      </nav>
      <motion.a
        href={TEL_LINK}
        data-testid="header-call-button"
        aria-label={`Ligar agora para ${PHONE_DISPLAY}`}
        whileTap={{ scale: 0.96 }}
        className="ml-auto flex items-center gap-2 bg-yellow-400 text-black font-extrabold text-sm md:text-base px-4 md:px-6 py-2.5 md:py-3 rounded-full hover:bg-yellow-300 transition-colors"
      >
        <Phone className="w-4 h-4" strokeWidth={2.5} />
        <span className="hidden sm:inline">{PHONE_DISPLAY}</span>
        <span className="sm:hidden">Ligar</span>
      </motion.a>
    </div>
  </motion.header>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="inicio" ref={ref} className="relative min-h-screen flex flex-col overflow-hidden grain-overlay" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src="/assets/hero-truck.jpeg"
          alt="Guincho Zero 21 na orla do Rio de Janeiro - Reboque 24 horas"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/15" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-black/40" />
      </motion.div>

      <motion.div style={{ opacity: contentOpacity }} className="relative z-10 flex-1 flex items-center max-w-7xl mx-auto w-full px-5 md:px-8 pt-28 pb-16">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
            className="inline-flex items-center gap-2 border border-yellow-400/50 bg-yellow-400/10 backdrop-blur-sm rounded-full px-4 py-2 mb-8"
            data-testid="hero-24h-badge"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-yellow-400" />
            </span>
            <span className="text-yellow-400 text-xs md:text-sm font-bold tracking-[0.2em] uppercase">
              Atendimento 24 horas • 7 dias por semana
            </span>
          </motion.div>

          <h1 className="font-display uppercase leading-[0.9] tracking-tight text-[17vw] sm:text-7xl md:text-8xl lg:text-9xl">
            <RevealLine delay={0.4}>
              <span className="text-white">Reboque</span>
            </RevealLine>
            <RevealLine delay={0.55}>
              <span className="text-yellow-400">24 Horas</span>
            </RevealLine>
          </h1>

          <RevealLine delay={0.75} className="mt-6">
            <p className="text-xl md:text-2xl lg:text-3xl font-bold text-white">
              Seu veículo precisa de ajuda? <span className="text-yellow-400">Nós resolvemos.</span>
            </p>
          </RevealLine>
          <RevealLine delay={0.9} className="mt-4">
            <p className="text-base md:text-lg text-zinc-300 max-w-xl leading-relaxed">
              Atendimento rápido, seguro e confiável para remoção e transporte de veículos em todo o Rio de Janeiro. Guincho 24 horas, perto de você.
            </p>
          </RevealLine>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <motion.a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              aria-label="Chamar no WhatsApp"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="gold-glow inline-flex items-center justify-center gap-3 bg-yellow-400 text-black font-display uppercase tracking-wide text-xl md:text-2xl px-8 md:px-12 py-5 md:py-6 rounded-xl hover:bg-yellow-300 transition-colors"
            >
              <MessageCircle className="w-7 h-7" strokeWidth={2.2} />
              Chamar no WhatsApp
            </motion.a>
            <motion.a
              href={TEL_LINK}
              data-testid="hero-call-button"
              aria-label={`Ligar agora para ${PHONE_DISPLAY}`}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center justify-center gap-3 bg-white/5 backdrop-blur-md border-2 border-white/25 text-white font-display uppercase tracking-wide text-xl md:text-2xl px-8 md:px-12 py-5 md:py-6 rounded-xl hover:border-yellow-400 hover:text-yellow-400 transition-colors"
            >
              <Phone className="w-7 h-7" strokeWidth={2.2} />
              Ligar Agora
            </motion.a>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

const Marquee = () => {
  const row = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="relative bg-yellow-400 py-4 md:py-5 overflow-hidden -rotate-1 scale-105 border-y-4 border-black" data-testid="services-marquee" aria-hidden="true">
      <div className="flex whitespace-nowrap animate-marquee w-max">
        {[...row, ...row].map((item, i) => (
          <span key={i} className="flex items-center font-display uppercase text-2xl md:text-4xl text-black tracking-wide">
            <span className="mx-6">{item}</span>
            <span className="w-3 h-3 rounded-full bg-black inline-block" />
          </span>
        ))}
      </div>
    </div>
  );
};

const Services = () => (
  <section id="servicos" className="relative py-24 md:py-32 bg-[#050505]" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <FadeUp>
        <span className="text-yellow-400 font-bold tracking-[0.3em] uppercase text-sm">O que fazemos</span>
        <h2 className="font-display uppercase text-4xl md:text-6xl lg:text-7xl text-white mt-4 leading-[0.95]">
          Serviços de <span className="text-stroke-gold">guincho</span> e reboque
        </h2>
        <p className="text-zinc-400 text-base md:text-lg max-w-2xl mt-6 leading-relaxed">
          Procurando <strong className="text-zinc-200">reboque 24 horas</strong> ou <strong className="text-zinc-200">guincho perto de mim</strong> no Rio de Janeiro? A Zero 21 cobre todas as situações: da remoção de veículos ao socorro automotivo 24 horas.
        </p>
      </FadeUp>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mt-14">
        {SERVICES.map((s, i) => (
          <FadeUp key={s.title} delay={(i % 3) * 0.1}>
            <div
              data-testid={`service-card-${String(i + 1).padStart(2, "0")}`}
              className="group relative bg-[#121212] border border-zinc-800 rounded-2xl p-6 md:p-7 h-full transition-all duration-300 hover:-translate-y-1 hover:border-yellow-400/60 hover:shadow-[0_10px_50px_-15px_rgba(250,204,21,0.25)]"
            >
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-xl bg-yellow-400/10 border border-yellow-400/20 flex items-center justify-center group-hover:bg-yellow-400 group-hover:text-black text-yellow-400 transition-colors duration-300">
                  <s.icon className="w-6 h-6" strokeWidth={2} />
                </div>
                <span className="font-display text-zinc-800 group-hover:text-yellow-400/30 text-3xl transition-colors duration-300">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-white mt-5">{s.title}</h3>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.desc}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Differentials = () => (
  <section id="diferenciais" className="relative py-24 md:py-32 bg-[#0a0a0a] border-y border-zinc-800/60" data-testid="differentials-section">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <FadeUp>
        <span className="text-yellow-400 font-bold tracking-[0.3em] uppercase text-sm">Por que a Zero 21</span>
        <h2 className="font-display uppercase text-4xl md:text-6xl lg:text-7xl text-white mt-4 leading-[0.95]">
          Confiança que <span className="text-yellow-400">move</span>
        </h2>
      </FadeUp>

      <div className="mt-16 space-y-0">
        {DIFFERENTIALS.map((d, i) => (
          <FadeUp key={d.title} delay={0.05 * i}>
            <div
              data-testid={`differential-item-${String(i + 1).padStart(2, "0")}`}
              className="group relative flex flex-col md:flex-row md:items-center gap-4 md:gap-10 py-10 md:py-12 border-t border-zinc-800 last:border-b overflow-hidden"
            >
              <span className="font-display text-7xl md:text-9xl leading-none text-yellow-400/15 group-hover:text-yellow-400/30 transition-colors duration-500 select-none md:w-56 shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="flex-1">
                <div className="flex items-center gap-4">
                  <d.icon className="w-7 h-7 text-yellow-400 shrink-0" strokeWidth={2} />
                  <h3 className="font-display uppercase text-2xl md:text-4xl text-white tracking-wide">{d.title}</h3>
                </div>
                <p className="text-zinc-400 text-base md:text-lg mt-3 max-w-2xl leading-relaxed">{d.desc}</p>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const ArtBanner = () => (
  <section className="relative bg-black py-20 md:py-28 overflow-hidden grain-overlay" data-testid="art-banner-section">
    <div className="max-w-7xl mx-auto px-5 md:px-8 flex justify-center">
      <FadeUp className="w-full max-w-xl">
        <div className="relative rounded-3xl overflow-hidden border border-yellow-400/30 gold-glow">
          <img
            src="/assets/banner-24h.jpeg"
            alt="Arte Zero 21 Reboque 24H - Retiramos seu veículo de pátios, leilões e cegonhas. Chama no WhatsApp 21 97457-2840"
            className="w-full h-auto block"
          />
        </div>
      </FadeUp>
    </div>
  </section>
);

const Coverage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="cobertura" ref={ref} className="relative py-24 md:py-32 bg-[#050505] overflow-hidden" data-testid="coverage-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <FadeUp>
          <span className="text-yellow-400 font-bold tracking-[0.3em] uppercase text-sm">Área de cobertura</span>
          <h2 className="font-display uppercase text-4xl md:text-6xl text-white mt-4 leading-[0.95]">
            Todo o <span className="text-yellow-400">Rio de Janeiro</span>
          </h2>
          <p className="text-zinc-400 text-base md:text-lg mt-6 leading-relaxed max-w-xl">
            Esteja onde estiver na capital carioca, nosso guincho chega até você. Cobertura completa nas quatro zonas da cidade, 24 horas por dia.
          </p>
          <div className="mt-10 space-y-5">
            {COVERAGE_ZONES.map((z) => (
              <div key={z.zone} data-testid={`coverage-zone-${z.zone.toLowerCase().replace(/\s/g, "-")}`} className="flex gap-4 items-start">
                <MapPin className="w-5 h-5 text-yellow-400 mt-1 shrink-0" strokeWidth={2.2} />
                <div>
                  <h3 className="font-bold text-white text-lg">{z.zone}</h3>
                  <p className="text-zinc-400 text-sm mt-0.5">{z.places}</p>
                </div>
              </div>
            ))}
          </div>
          <motion.a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="coverage-whatsapp-button"
            whileTap={{ scale: 0.97 }}
            className="inline-flex items-center gap-3 mt-10 bg-yellow-400 text-black font-display uppercase tracking-wide text-lg px-8 py-4 rounded-xl hover:bg-yellow-300 transition-colors"
          >
            <MessageCircle className="w-6 h-6" strokeWidth={2.2} />
            Solicitar Reboque
          </motion.a>
        </FadeUp>

        <FadeUp delay={0.15} className="relative">
          <div className="relative rounded-3xl overflow-hidden border border-zinc-800 aspect-[4/3] lg:aspect-auto lg:h-[560px]">
            <motion.img
              style={{ y: imgY }}
              src="/assets/truck-rio.jpeg"
              alt="Guincho Zero 21 no Rio de Janeiro com o Pão de Açúcar ao fundo - reboque 24 horas"
              className="w-full h-[115%] object-cover scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 bg-black/70 backdrop-blur-md border border-yellow-400/30 rounded-2xl px-5 py-4">
              <Clock className="w-6 h-6 text-yellow-400 shrink-0" strokeWidth={2.2} />
              <p className="text-white font-bold text-sm md:text-base">
                Atendimento 24 horas <span className="text-yellow-400">•</span> 7 dias por semana
              </p>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

const Conversion = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="contato" ref={ref} className="relative py-24 md:py-36 bg-yellow-400 overflow-hidden grain-overlay" data-testid="conversion-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <FadeUp>
          <h2 className="font-display uppercase text-black text-5xl md:text-7xl lg:text-8xl leading-[0.9]">
            Precisa de um reboque <span className="text-stroke-white">agora?</span>
          </h2>
          <p className="text-black/70 font-semibold text-lg md:text-xl mt-6 max-w-lg leading-relaxed">
            Não fique esperando. Entre em contato e solicite atendimento imediato.
          </p>
          <motion.a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="conversion-whatsapp-button"
            aria-label="Chamar Zero 21 no WhatsApp"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="inline-flex items-center justify-center gap-3 mt-10 bg-black text-yellow-400 font-display uppercase tracking-wide text-2xl md:text-3xl px-10 md:px-14 py-6 md:py-7 rounded-2xl hover:bg-zinc-900 transition-colors shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)]"
          >
            <MessageCircle className="w-8 h-8" strokeWidth={2.2} />
            Chamar Zero 21 no WhatsApp
          </motion.a>
          <a
            href={TEL_LINK}
            data-testid="conversion-phone-link"
            className="flex items-center gap-3 mt-6 text-black font-extrabold text-xl md:text-2xl hover:text-zinc-800 transition-colors"
          >
            <Phone className="w-6 h-6" strokeWidth={2.5} />
            {PHONE_DISPLAY}
          </a>
        </FadeUp>

        <FadeUp delay={0.15}>
          <div className="relative rounded-3xl overflow-hidden border-4 border-black aspect-[4/3] lg:h-[480px]">
            <motion.img
              style={{ y: imgY }}
              src="/assets/truck-street.jpeg"
              alt="Caminhão guincho Zero 21 pronto para reboque de veículos nas ruas do Rio de Janeiro"
              className="w-full h-[115%] object-cover scale-110"
            />
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="bg-black border-t border-zinc-800 pt-16 pb-8" data-testid="footer">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid md:grid-cols-3 gap-10 md:gap-8">
        <div>
          <div className="flex items-center">
            <img src="/assets/logo.png" alt="Logo Zero 21 Reboque 24H" className="h-20 w-auto" />
          </div>
          <p className="text-zinc-400 text-sm mt-5 leading-relaxed max-w-xs">
            Reboque, guincho 24 horas, remoção e transporte de veículos em todo o Rio de Janeiro. Rápido, seguro e confiável.
          </p>
        </div>

        <div>
          <h3 className="font-display uppercase text-white text-lg tracking-wide mb-5">Contato</h3>
          <ul className="space-y-4 text-sm">
            <li>
              <a href={TEL_LINK} data-testid="footer-phone-link" className="flex items-center gap-3 text-zinc-300 hover:text-yellow-400 transition-colors">
                <Phone className="w-4 h-4 text-yellow-400" strokeWidth={2.2} />
                Telefone: {PHONE_DISPLAY}
              </a>
            </li>
            <li>
              <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="flex items-center gap-3 text-zinc-300 hover:text-yellow-400 transition-colors">
                <MessageCircle className="w-4 h-4 text-yellow-400" strokeWidth={2.2} />
                WhatsApp: {PHONE_DISPLAY}
              </a>
            </li>
            <li className="flex items-center gap-3 text-zinc-300" data-testid="footer-hours-info">
              <Clock className="w-4 h-4 text-yellow-400" strokeWidth={2.2} />
              Atendimento 24 horas, 7 dias por semana
            </li>
            <li className="flex items-center gap-3 text-zinc-300" data-testid="footer-coverage-info">
              <MapPin className="w-4 h-4 text-yellow-400" strokeWidth={2.2} />
              Rio de Janeiro — RJ (capital inteira)
            </li>
          </ul>
        </div>

        <div>
          <h3 className="font-display uppercase text-white text-lg tracking-wide mb-5">Serviços</h3>
          <ul className="grid grid-cols-1 gap-2.5 text-sm text-zinc-400">
            <li>Reboque 24 horas e remoção de veículos</li>
            <li>Retirada de veículo de pátio e leilão</li>
            <li>Transporte de veículos e alto valor</li>
            <li>Recarga de bateria e abertura de veículos</li>
            <li>Destombamento e SOS automotivo</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-zinc-800 mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
        <p className="text-zinc-500 text-xs">© 2026 Zero 21 Reboque 24H — Todos os direitos reservados.</p>
        <p className="text-zinc-600 text-xs">Guincho 24 horas no Rio de Janeiro • Emergências: {PHONE_DISPLAY}</p>
      </div>
    </div>
  </footer>
);

const WhatsAppFab = () => (
  <motion.a
    href={WA_LINK}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="whatsapp-fab-button"
    aria-label="Chamar no WhatsApp - Preciso de socorro"
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: 1.6, type: "spring", stiffness: 260, damping: 18 }}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.92 }}
    className="fab-pulse fixed bottom-5 right-5 md:bottom-8 md:right-8 z-50 w-16 h-16 md:w-[72px] md:h-[72px] rounded-full bg-[#25D366] flex items-center justify-center"
  >
    <MessageCircle className="w-8 h-8 text-white" strokeWidth={2.2} fill="white" />
  </motion.a>
);

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    const anchors = document.querySelectorAll('a[href^="#"]');
    const onClick = (e) => {
      const href = e.currentTarget.getAttribute("href");
      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        lenis.scrollTo(target, { offset: -72 });
      }
    };
    anchors.forEach((a) => a.addEventListener("click", onClick));

    return () => {
      cancelAnimationFrame(rafId);
      anchors.forEach((a) => a.removeEventListener("click", onClick));
      lenis.destroy();
    };
  }, []);

  return (
    <div className="bg-[#050505] min-h-screen">
      <Header />
      <main>
        <Hero />
        <Marquee />
        <Services />
        <Differentials />
        <ArtBanner />
        <Coverage />
        <Conversion />
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
